import { useState } from "react";
import { Box, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { Field, FormInput } from "@/components/jobOffer/ui";
import { SectionCard, SectionHeader } from "@/components/jobOffer/ui/layout";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { CampaignService } from "@/services/CampaignService";
import { CHANNEL_OPTIONS } from "@/components/campaign/channelOptions";
import { LEAD_STATUS_OPTIONS } from "@/components/campaign/leadStatusOptions";

const channelLabel = (value) => CHANNEL_OPTIONS.find(o => o.value === value)?.label ?? value;
const statusLabel = (value) => LEAD_STATUS_OPTIONS.find(o => o.value === value)?.label ?? value;

const ResultRow = ({ label, value }) => (
    <HStack justify="space-between" gap={4} py={1.5} borderBottom="1px solid" borderColor="border.primary">
        <Text fontSize="sm" color="text.grey">{label}</Text>
        <Text fontSize="sm" color="text.white" fontWeight={500} textAlign="right">{value}</Text>
    </HStack>
);

const StepTest = ({ values }) => {
    const [testContact, setTestContact] = useState("");
    const [isRunning, setIsRunning] = useState(false);
    const [result, setResult] = useState(null);

    const campaignId = values.id;

    const runSimulation = async () => {
        if (!testContact.trim()) {
            toaster.create({ type: "error", title: "Podaj telefon lub e-mail testowy" });
            return;
        }
        setIsRunning(true);
        setResult(null);
        try {
            const data = await CampaignService.simulate(campaignId, testContact.trim());
            setResult(data);
        } catch (error) {
            toaster.create({
                type: "error",
                title: extractBusinessErrorMessage(error) ?? "Nie udało się uruchomić symulacji",
            });
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <VStack w="full" align="stretch" gap={4}>
            <SectionCard>
                <SectionHeader number={22} title="Symulacja przepływu" subtitle={`Test pełnego procesu dla kampanii: ${values.name || "(bez nazwy)"}`} />
                <VStack align="stretch" gap={4}>
                    {!campaignId ? (
                        <Text fontSize="sm" color="text.grey">
                            Zapisz kampanię (np. "Zapisz jako szkic"), żeby móc uruchomić symulację -- potrzebny jest już zapisany config.
                        </Text>
                    ) : (
                        <>
                            <Field label="Telefon lub e-mail testowy">
                                <FormInput value={testContact} onChange={(e) => setTestContact(e.target.value)} placeholder="np. +48 500 000 000 lub test@example.com" />
                            </Field>
                            <Box
                                as="button" type="button" onClick={runSimulation} disabled={isRunning}
                                alignSelf="flex-start" px={5} py={2.5} borderRadius={8} fontSize="sm" fontWeight={600}
                                bg="purple.500" color="white" opacity={isRunning ? 0.6 : 1}
                                display="flex" alignItems="center" gap={2}
                            >
                                {isRunning && <Spinner size="xs" />}
                                ▶ Uruchom symulację
                            </Box>
                        </>
                    )}
                </VStack>
            </SectionCard>

            {result && (
                <SectionCard>
                    <SectionHeader number={23} title="Wynik symulacji" subtitle={`Testowy kontakt: ${result.testContact}`} />
                    <VStack align="stretch" gap={4}>
                        <Box>
                            <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                Dopasowana reguła statusu
                            </Text>
                            {result.matchedStatus ? (
                                <VStack align="stretch" gap={0}>
                                    <ResultRow label="Dopasowany status" value={statusLabel(result.matchedStatus)} />
                                    <ResultRow label="Akcja agenta" value={result.matchedAgentAction || "--"} />
                                    <ResultRow label="Kanał podstawowy" value={result.resolvedPrimaryChannel ? channelLabel(result.resolvedPrimaryChannel) : "--"} />
                                    <ResultRow label="Kanał dodatkowy" value={result.resolvedFallbackChannel ? channelLabel(result.resolvedFallbackChannel) : "brak"} />
                                    <ResultRow label="Opóźnienie" value={result.delayMinutes != null ? `${result.delayMinutes} min` : "--"} />
                                </VStack>
                            ) : (
                                <Text fontSize="sm" color="text.grey">Brak aktywnej reguły statusu w konfiguracji tej kampanii.</Text>
                            )}
                        </Box>

                        {result.startMessageTemplate && (
                            <Box>
                                <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                    Treść komunikatu startowego
                                </Text>
                                <Box bg="bg.dark" borderWidth={1} borderColor="border.primary" borderRadius={8} p={3}>
                                    <Text fontSize="sm" color="text.white" whiteSpace="pre-wrap">{result.startMessageTemplate}</Text>
                                </Box>
                            </Box>
                        )}

                        <Box>
                            <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                Mapowanie pól ({(result.leadFieldMappings?.length ?? 0) + (result.customFieldMappings?.length ?? 0)})
                            </Text>
                            {(result.leadFieldMappings?.length || result.customFieldMappings?.length) ? (
                                <VStack align="stretch" gap={0}>
                                    {(result.leadFieldMappings ?? []).map((m, i) => (
                                        <ResultRow key={`sys-${i}`} label={m.systemField} value={m.sourceColumn} />
                                    ))}
                                    {(result.customFieldMappings ?? []).map((m, i) => (
                                        <ResultRow key={`custom-${i}`} label={m.fieldName} value={m.sourceColumn} />
                                    ))}
                                </VStack>
                            ) : (
                                <Text fontSize="sm" color="text.grey">Brak skonfigurowanego mapowania danych leada.</Text>
                            )}
                        </Box>
                    </VStack>
                </SectionCard>
            )}
        </VStack>
    );
};

export default StepTest;
